import { FrameForm } from '@studiometa/ui';

const STORAGE_KEY = 'signData';

/**
 * SignForm component - extends FrameForm to handle birthdate form and zodiac sign calculation.
 * Updates form action dynamically based on selected day and month.
 */
export default class SignForm extends FrameForm {
  /**
   * Config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    ...FrameForm.config,
    name: 'SignForm',
    refs: [...FrameForm.config.refs],
    options: {
      ...FrameForm.config.options,
      signsPath: {
        type: String,
        default: '/signs',
      },
      redirectUrl: {
        type: String,
        default: '/wishcard/',
      },
    },
  };

  /**
   * Get day input element.
   * @returns {HTMLInputElement|null}
   */
  get dayInput() {
    return this.$el.querySelector('input[name="day"]');
  }

  /**
   * Get month input element.
   * @returns {HTMLInputElement|null}
   */
  get monthInput() {
    return this.$el.querySelector('input[name="month"]');
  }

  /**
   * Calculate zodiac sign from day and month.
   * @param {number} day Day of birth
   * @param {number} month Month of birth (1-12)
   * @returns {string} Zodiac sign key
   */
  getZodiacSign(day, month) {
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'belier';
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'taureau';
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'gemeaux';
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'cancer';
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'lion';
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'vierge';
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'balance';
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'scorpion';
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'sagittaire';
    if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'capricorne';
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'verseau';
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return 'poisson';
    return null;
  }

  /**
   * Get current sign based on day/month inputs.
   * @returns {string|null} Sign key or null if inputs not valid
   */
  getCurrentSign() {
    const day = Number.parseInt(this.dayInput?.value, 10);
    const month = Number.parseInt(this.monthInput?.value, 10);

    if (Number.isNaN(day) || Number.isNaN(month)) {
      return null;
    }

    return this.getZodiacSign(day, month);
  }

  /**
   * Update the form action URL based on selected day and month.
   */
  updateFormAction() {
    const sign = this.getCurrentSign();
    if (!sign) {
      return;
    }

    const form = this.$el;
    const redirectUrl = this.getRedirectUrl();

    form.action = redirectUrl.pathname + redirectUrl.search;
  }

  /**
   * Build the redirect URL from options.
   * @returns {URL} The redirect URL object
   */
  getRedirectUrl() {
    return new URL(String(this.$options.redirectUrl), window.location.origin);
  }

  /**
   * Bound update handler.
   */
  handleInputChange = () => {
    this.updateFormAction();
  };

  /**
   * Mounted lifecycle hook - add change listeners to inputs.
   */
  mounted() {
    this.dayInput?.addEventListener('change', this.handleInputChange);
    this.monthInput?.addEventListener('change', this.handleInputChange);
  }

  /**
   * Destroyed lifecycle hook - remove change listeners.
   */
  destroyed() {
    this.dayInput?.removeEventListener('change', this.handleInputChange);
    this.monthInput?.removeEventListener('change', this.handleInputChange);
  }

  /**
   * Request gyroscope permission (iOS 13+).
   * @returns {Promise<boolean>} Whether permission was granted
   */
  async #requestGyroPermission() {
    if (!window.DeviceOrientationEvent) return false;

    // @ts-ignore - requestPermission is iOS-specific
    if (typeof DeviceOrientationEvent.requestPermission !== 'function') {
      return true;
    }

    try {
      // @ts-ignore
      const permission = await DeviceOrientationEvent.requestPermission();
      return permission === 'granted';
    } catch {
      return false;
    }
  }

  /**
   * Handle form submit - fetch JSON, save to localStorage, then navigate via Frame.
   * @param {{ event: SubmitEvent }} props
   */
  async onSubmit({ event }) {
    event.preventDefault();

    const sign = this.getCurrentSign();
    if (!sign) {
      return;
    }

    await this.#requestGyroPermission();

    const signUrl = `${String(this.$options.signsPath)}/${sign}.json`;
    const response = await fetch(signUrl, { cache: 'no-store' });
    const signData = await response.json();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(signData));

    const frame = this.$frame;
    const redirectUrl = this.getRedirectUrl();

    if (frame) {
      frame.fetch(redirectUrl);
    } else {
      window.location.href = redirectUrl.href;
    }
  }
}
